import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Send, Trash2, Phone, Building, Box, MessageSquare, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import PageHead from '@/components/PageHead';
import { adminGetQuote, adminUpdateStatus, adminReplyQuote, adminDeleteQuote } from '@/lib/api';

const STATUS_LABEL = { new: 'Nouveau', in_progress: 'En cours', replied: 'Répondu', archived: 'Archivé' };
const STATUS_COLOR = {
  new: 'bg-[hsl(186_45%_92%)] text-[hsl(186_55%_28%)] border-[hsl(186_45%_70%)]',
  in_progress: 'bg-amber-50 text-amber-800 border-amber-200',
  replied: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  archived: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function AdminQuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = await adminGetQuote(id);
      setQuote(q);
      setSubject(`Votre demande de devis - ${q.product_name || 'ALOFI SA'}`);
      setBody(`Bonjour ${q.name},\n\nNous vous remercions pour votre intérêt pour ALOFI SA.\n\nSuite à votre demande${q.product_name ? ` concernant "${q.product_name}"` : ''}, nous avons le plaisir de vous adresser notre proposition :\n\n- Quantité : ${q.quantity || 'à préciser'}\n- Délai indicatif : sous 5-7 jours ouvrés\n- Conditions : tarification dégrressive selon volume\n\nNous restons à votre disposition pour tout complément d'information.\n\nCordialement,`);
    } catch (e) {
      toast.error('Demande introuvable');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (status) => {
    await adminUpdateStatus(id, status);
    toast.success('Statut mis à jour');
    load();
  };

  const sendReply = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Sujet et message obligatoires');
      return;
    }
    setSending(true);
    try {
      await adminReplyQuote(id, subject, body);
      toast.success('Email envoyé au client');
      load();
    } catch (e) {
      toast.error('Échec envoi de l’email');
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    await adminDeleteQuote(id);
    toast.success('Demande supprimée');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-quote-detail-page">
      <PageHead title={quote ? `Devis · ${quote.name}` : 'Demande de devis'} />
      <header className="sticky top-0 z-30 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <Logo size="sm" />
          </Link>
          <Button asChild variant="ghost" size="sm"><Link to="/admin"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Link></Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading || !quote ? (
          <div className="grid md:grid-cols-3 gap-6">
            <Skeleton className="h-64 md:col-span-1" /><Skeleton className="h-64 md:col-span-2" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* INFO PANEL */}
            <Card className="md:col-span-1 rounded-2xl border-border p-6 h-fit">
              <div className="flex items-center justify-between gap-3">
                <Badge variant="outline" className={STATUS_COLOR[quote.status]} data-testid="admin-quote-status-badge">{STATUS_LABEL[quote.status]}</Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="rounded-lg" data-testid="admin-status-menu-button">Changer statut <ChevronDown className="w-4 h-4 ml-1" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <DropdownMenuItem key={k} onClick={() => updateStatus(k)} data-testid={`admin-set-status-${k}`}>{v}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-semibold tracking-tight">{quote.name}</h1>
              <a href={`mailto:${quote.email}`} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mt-1">
                <Mail className="w-3.5 h-3.5" />{quote.email}
              </a>
              <Separator className="my-4" />
              <ul className="space-y-3 text-sm">
                {quote.phone && <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />{quote.phone}</li>}
                {quote.company && <li className="flex items-start gap-2"><Building className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />{quote.company}</li>}
                {quote.product_name && <li className="flex items-start gap-2"><Box className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" /><span>{quote.product_name}{quote.quantity && <span className="text-muted-foreground"> · Qté : {quote.quantity}</span>}</span></li>}
                <li className="flex items-start gap-2"><MessageSquare className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
                  <div className="whitespace-pre-wrap">{quote.message || <span className="text-muted-foreground">Pas de message</span>}</div>
                </li>
              </ul>
              <Separator className="my-4" />
              <div className="text-xs text-muted-foreground font-mono">Reçu le {formatDate(quote.created_at)}</div>
              <div className="text-[10px] text-muted-foreground font-mono break-all mt-1">REF : {quote.id}</div>
              <Button onClick={remove} variant="ghost" size="sm" className="mt-4 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="admin-quote-delete"><Trash2 className="w-4 h-4 mr-1" />Supprimer</Button>
            </Card>

            {/* REPLY COMPOSER */}
            <Card className="md:col-span-2 rounded-2xl border-border p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold">Répondre par email</h2>
                <span className="text-xs text-muted-foreground">via SMTP IONOS • contact@alofisa.eu</span>
              </div>
              <Separator className="my-4" />
              <div className="space-y-3">
                <div>
                  <Label htmlFor="to">Destinataire</Label>
                  <Input id="to" value={quote.email} readOnly className="mt-1 bg-secondary" />
                </div>
                <div>
                  <Label htmlFor="subj">Sujet</Label>
                  <Input id="subj" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="admin-reply-subject-input" />
                </div>
                <div>
                  <Label htmlFor="body">Message</Label>
                  <Textarea id="body" rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="mt-1" data-testid="admin-reply-body-input" />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button variant="secondary" onClick={() => updateStatus('in_progress')} className="rounded-xl" data-testid="admin-mark-in-progress">Marquer en cours</Button>
                  <Button onClick={sendReply} disabled={sending} className="rounded-xl bg-[hsl(186_55%_38%)] hover:bg-[hsl(186_55%_32%)]" data-testid="admin-reply-email-send-button">
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Envoyer la réponse
                  </Button>
                </div>
              </div>
              {quote.replies?.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">Historique des réponses</h3>
                  <ul className="mt-3 space-y-3">
                    {quote.replies.map((r, i) => (
                      <li key={i} className="rounded-xl border border-border p-4 bg-secondary/40">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>De {r.sent_by}</span>
                          <span className="font-mono">{formatDate(r.sent_at)}</span>
                        </div>
                        <div className="mt-2 font-medium text-sm">{r.subject}</div>
                        <div className="mt-1 text-sm whitespace-pre-wrap text-foreground/85">{r.body}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
